"use client";

import React, { useState, useEffect } from "react";
import { Search, SlidersHorizontal, ArrowRight, ShieldCheck, Zap, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch, buildProductSearchUrl } from "@/components/shop/ProductsSearch";
declare global {
  interface Window {
    wvcClient?: any;
  }
}
function SearchResultsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="py-4 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          Scanning inventory...
        </p>
      </div>;
  }
  if (error) {
    return <div className="py-4 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-destructive">
          Failed to load equipment specs.
        </p>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="py-4 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          No matching machinery or parts found.
        </p>
      </div>;
  }
  const visibleProducts = products.slice(0, 6);
  return <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {visibleProducts.map((product, i) => <Link key={product.id} to={product.link || "#"} data-index={i} className="group flex items-center gap-3 border border-border bg-background p-2 transition-colors hover:border-foreground">
          {product.featured_image_url ? <div className="h-12 w-12 shrink-0 overflow-hidden bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
            </div> : <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-muted font-mono text-[10px] text-muted-foreground">
              SPEC
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate font-serif text-sm font-semibold uppercase tracking-wide text-foreground group-hover:text-primary">
              {product.title}
            </p>
            <div className="mt-0.5">
              <ProductPrice product={product} className="font-mono text-xs font-medium text-foreground" />
            </div>
          </div>
        </Link>)}
    </div>;
}
function HeroSearchModule() {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <div className="w-full max-w-2xl">
      <EcommerceProductsProvider wp_query={wp_query}>
        <Popover>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search models, voltage, or part numbers..." className="w-full font-sans text-sm" />
            </div>

            <PopoverTrigger asChild>
              <Button type="button" variant="default" className="rounded-none border border-primary bg-primary px-6 font-serif uppercase tracking-wider text-primary-foreground hover:bg-primary/90">
                <Search className="size-4" />
                Find equipment
              </Button>
            </PopoverTrigger>
          </div>

          <PopoverContent align="start" className="w-full min-w-[320px] max-w-[640px] space-y-4 rounded-none border border-border bg-background p-4 shadow-md sm:min-w-[480px]">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Inventory search results
              </span>
              {hasSearchQuery && <span className="font-mono text-xs text-primary">Live spec query</span>}
            </div>

            <div className="max-h-80 space-y-3 overflow-y-auto">
              {!hasSearchQuery ? <p className="py-4 text-center font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Enter a keyword, motor rating, or part SKU above to view specs.
                </p> : <>
                  <SearchResultsConsumer />
                  <div className="border-t border-border pt-2 text-right">
                    <Link to={buildProductSearchUrl(searchValue)} className="inline-flex items-center gap-1 font-serif text-sm font-semibold uppercase tracking-wider text-primary hover:underline">
                      View complete search catalog
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </>}
            </div>
          </PopoverContent>
        </Popover>
      </EcommerceProductsProvider>
    </div>;
}
export default function ShopHero() {
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined" && window.wvcClient) {
      const params = typeof window.wvcClient.getCurrentWPQueryParams === "function" ? window.wvcClient.getCurrentWPQueryParams() : {};
      const query = String(params?.s ?? params?.search ?? "").trim();
      setSearchTerm(query);
    }
  }, []);
  const quickLinks = [{
    label: "New bulls",
    to: "/NewMechanicalBullsPage"
  }, {
    label: "Used bulls",
    to: "/UsedMechanicalBullsPage"
  }, {
    label: "Parts & accessories",
    to: "/MechanicalBullPartsAndAccessoriesPage"
  }, {
    label: "Price guide",
    to: "/MechanicalBullPriceGuidePage"
  }];
  const specHighlights = [{
    icon: Zap,
    title: "Direct 220V & 110V specs",
    desc: "Full electrical documentation provided with every unit."
  }, {
    icon: ShieldCheck,
    title: "Commercial-grade steel",
    desc: "Designed for continuous duty in high-traffic commercial venues."
  }, {
    icon: Truck,
    title: "Nationwide freight",
    desc: "Crated delivery with dedicated logistics coordination."
  }];
  return <section id="shop-hero" data-nav="light" className="border-b border-border bg-background pt-12 pb-16 md:pt-16 md:pb-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Machinery Rating Plate Frame */}
        <div className="border border-border bg-card p-6 md:p-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl space-y-4">
              {/* Classification Tag */}
              <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1">
                <span className="h-1.5 w-1.5 bg-primary" />
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Commercial Equipment Inventory • U.S. Distribution
                </span>
              </div>

              {/* Dynamic or Default Heading */}
              <h1 className="font-serif text-3xl font-bold uppercase tracking-wide text-foreground sm:text-4xl md:text-5xl">
                {searchTerm ? `Search results for "${searchTerm}"` : "Commercial equipment catalog"}
              </h1>

              {/* Concrete Subheading */}
              <p className="max-w-2xl font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
                Inspect complete technical ratings, electrical phase specs, and freight schedules
                for commercial mechanical bulls, certified pre-owned units, and direct replacement components.
              </p>
            </div>

            {/* Quick Catalog Links */}
            <div className="flex flex-wrap gap-2 border-t border-border pt-4 lg:border-t-0 lg:pt-0">
              {quickLinks.map((item, index) => <Button key={item.label} data-index={index} variant="outline" asChild className="rounded-none border-border bg-muted/50 font-serif text-xs uppercase tracking-wider text-foreground hover:border-foreground hover:bg-background">
                  <Link to={item.to}>{item.label}</Link>
                </Button>)}
            </div>
          </div>

          {/* Search Interface Container */}
          <div className="mt-8 border-t border-border pt-8">
            <HeroSearchModule />
          </div>

          {/* Technical Spec Baseline Strip */}
          <div className="mt-10 grid grid-cols-1 gap-4 border-t border-border pt-8 sm:grid-cols-2 lg:grid-cols-3">
            {specHighlights.map((item, idx) => {
            const IconComponent = item.icon;
            return <div key={item.title} data-index={idx} className="flex items-start gap-3 border border-border bg-muted/30 p-4">
                  <div className="border border-border bg-background p-2 text-primary">
                    <IconComponent className="size-4" />
                  </div>
                  <div>
                    <h2 className="font-serif text-sm font-semibold uppercase tracking-wider text-foreground">
                      {item.title}
                    </h2>
                    <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                      {item.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}